'use client';

import type { ReactNode } from 'react';
// eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration than use()
import { createContext, useContext, useEffect, useState } from 'react';
import { useStore } from 'zustand';

import type { SpaceListItem } from '@/types/navigation';

import type { SpaceStoreApi } from './store';
import { createSpaceStore } from './store';
import type { SpaceStore } from './types';

// ------------- Context -------------

const SpaceStoreContext = createContext<SpaceStoreApi | undefined>(undefined);

// ------------- Provider Props -------------

type SpaceStoreProviderProps = {
  children: ReactNode;
  /** Initial space ID resolved from server (cookie/claims/API) */
  configSpaceId?: string | null;
  /** Initial available spaces from server (claims or API) */
  initialSpaces?: SpaceListItem[];
};

// ------------- Provider -------------

/**
 * Provider for space store context.
 *
 * Features:
 * - Initializes store with server-resolved space ID
 * - Cookie persistence handled by Zustand persist middleware
 * - Auto-selection of first space when needed (via store)
 * - SSR-compatible with proper hydration handling
 *
 * Usage:
 * ```tsx
 * <SpaceStoreProvider configSpaceId={resolvedSpaceId} initialSpaces={userSpaces}>
 *   <App />
 * </SpaceStoreProvider>
 * ```
 */
export function SpaceStoreProvider({
  children,
  configSpaceId,
  initialSpaces,
}: SpaceStoreProviderProps) {
  // Use useState with lazy initializer to create store once (avoids ref access during render)
  const [store] = useState<SpaceStoreApi>(() =>
    createSpaceStore({
      configSpaceId,
      initialSpaces,
    }),
  );

  // After client-side hydration:
  // 1. Mark store as hydrated so queries can be enabled
  // 2. Trigger auto-select if needed (handles edge cases)
  useEffect(() => {
    // Mark as hydrated - enables queries in useQueryConfig
    store.getState().setHydrated(true);

    // Trigger auto-select in case server didn't have complete info
    // This handles cases like:
    // - Cookie space no longer valid
    // - Spaces loaded after initial render
    store.getState().autoSelectFirstSpace();
  }, [store]);

  return (
    <SpaceStoreContext value={store}>
      {children}
    </SpaceStoreContext>
  );
}

// ------------- Hook to Access Store -------------

/**
 * Hook to select state from the space store.
 * Use with a selector for optimal performance.
 *
 * @example
 * ```tsx
 * const spaceId = useSpaceStore(state => state.currentSpaceId);
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSpaceStore<T>(selector: (state: SpaceStore) => T): T {
  // eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration
  const store = useContext(SpaceStoreContext);

  if (store === undefined) {
    throw new Error('useSpaceStore must be used within SpaceStoreProvider');
  }

  return useStore(store, selector);
}

// ------------- Direct Store Access (for non-React contexts) -------------

/**
 * Hook to access the raw store API.
 * Useful for imperative operations outside of React render.
 *
 * @example
 * ```tsx
 * const storeApi = useSpaceStoreApi();
 * // Later, in an event handler:
 * storeApi.getState().setCurrentSpace('new-space');
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSpaceStoreApi(): SpaceStoreApi {
  // eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration
  const store = useContext(SpaceStoreContext);

  if (store === undefined) {
    throw new Error('useSpaceStoreApi must be used within SpaceStoreProvider');
  }

  return store;
}
